import React, { useEffect, useState, useMemo } from 'react';
import ListIdentity from '../components/layout/ListIdentity';
import '../components/layout/AppPage.css';
import './WZDocuments.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Table, Button, App, Badge, Dropdown, Input, DatePicker, Row, Col, Modal, Tag, Tooltip, Segmented, Descriptions, Divider } from 'antd';
import type { MenuProps } from 'antd';
import { getWZErrorMessage, isBlockingWZError, WZ_ERROR_CODE_KEYS } from '../modules/warehouse/wzErrorMessages';
import { findBlockedAction, getBlockedActionMessage } from '../modules/warehouse/blockedActions';
import { useFeedback } from '../utils/feedback';
import { DeleteOutlined, FilePdfOutlined, EditOutlined, EyeOutlined, SearchOutlined, FileOutlined, FilterOutlined, MoreOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { authService } from '../services/auth';
import { formatDateEuropean } from '../utils/dateUtils';
import dayjs from 'dayjs';
import { WZDocumentDto, normalizeWZDocuments } from '../dto/wz';
import {
  canDeleteWarehouseDocument,
  canManageWZDocuments,
} from '../modules/warehouse/warehouseDocumentAccessPolicy';
import { getAuthFileUrl } from '../utils/fileUrl';
import { canShowWZEditAction } from '../utils/wzEditRules';
import { usePageAutoRefresh } from '../hooks/usePageAutoRefresh';
import WarehouseDocumentsTabs from '../modules/warehouse/components/WarehouseDocumentsTabs';
import { compareMonthlyDocumentNumbers } from '../utils/documentNumber';
import {
  WAREHOUSE_DOCUMENT_LIST_VIEW_PARAM,
  getCancelledDocumentKind,
  parseWarehouseDocumentListView,
  partitionWarehouseDocumentsByView,
  type WarehouseDocumentListView,
} from '../modules/warehouse/warehouseDocumentListView';
import { getTransportLabel } from '../utils/transportLabel';
import ProductNameWithDimensions from '../components/products/ProductNameWithDimensions';

const { RangePicker } = DatePicker;

const WZDocuments: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation(['wz', 'common']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [wzDocuments, setWzDocuments] = useState<WZDocumentDto[]>([]);
  // "Aktywne" vs "Anulowane i storno" lives in the URL (?view=cancelled) like
  // other list filters, so links and reloads land on the same category.
  const listView = parseWarehouseDocumentListView(searchParams.get(WAREHOUSE_DOCUMENT_LIST_VIEW_PARAM));
  const handleListViewChange = (value: WarehouseDocumentListView) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'active') {
      next.delete(WAREHOUSE_DOCUMENT_LIST_VIEW_PARAM);
    } else {
      next.set(WAREHOUSE_DOCUMENT_LIST_VIEW_PARAM, value);
    }
    setSearchParams(next);
  };
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<WZDocumentDto | null>(null);

  const currentUser = authService.getCurrentUser();
  const canEdit = canManageWZDocuments(currentUser);
  const canDelete = canDeleteWarehouseDocument(currentUser);
  const canDownloadWZPdf = authService.canDownloadWZDocumentPdf();
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Filter states
  const [filters, setFilters] = useState<{
    dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
    search: string;
  }>({
    dateRange: null,
    search: ''
  });

  useEffect(() => {
    fetchWZDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWZDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get('/wz');
      setWzDocuments(normalizeWZDocuments(response.data, 'wz.list'));
    } catch (error: any) {
      feedback.error(getWZErrorMessage(error, t, 'wz:error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  usePageAutoRefresh(fetchWZDocuments);

  const handleDelete = async (id: string, wzNumber: string) => {
    try {
      await api.delete(`/wz/${id}`);
      feedback.success(t('wz:success.deleted', { wzNumber }));
      fetchWZDocuments();
    } catch (error: any) {
      const data = error.response?.data;
      // With `relatedDocuments` the shared mapper names the PZ batches the WZ
      // consumed; the status-only sentence remains for older responses.
      if (data?.code === 'WZ_DELETE_BLOCKED_STATUS' && !(Array.isArray(data.relatedDocuments) && data.relatedDocuments.length > 0)) {
        // Interpolates the blocking status, so it cannot live in the shared
        // static code map — and it is a hard block, so it waits to be read.
        const statusLabel = data.status
          ? t(`wz:status.${String(data.status).toLowerCase()}`)
          : '';
        feedback.error(
          t('wz:error.deleteBlockedStatus', { wzNumber, status: statusLabel }),
          { sticky: true },
        );
      } else {
        feedback.error(getWZErrorMessage(error, t, 'wz:error.deleteFailed', { wzNumber }), {
          sticky: isBlockingWZError(error),
        });
      }
    }
  };

  const handleDownloadPDF = async (id: string, wzNumber: string) => {
    setDownloading(id);
    try {
      const response = await api.get(`/wz/${id}/pdf`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${wzNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      feedback.success(t('wz:success.pdfDownloaded', { wzNumber }));
    } catch (error: any) {
      feedback.error(getWZErrorMessage(error, t, 'wz:error.pdfGenerateFailed'));
    } finally {
      setDownloading(null);
    }
  };

  const handlePreviewDocument = (doc: WZDocumentDto) => {
    setPreviewDoc(doc);
    setPreviewVisible(true);
  };

  const documentsByView = useMemo(() => partitionWarehouseDocumentsByView(wzDocuments), [wzDocuments]);
  const cancelledCount = documentsByView.cancelled.length;

  // Filter documents based on current filters
  const filteredDocuments = useMemo(() => {
    let filtered = listView === 'cancelled' ? [...documentsByView.cancelled] : [...documentsByView.active];

    // Date range filter
    if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
      const startDate = filters.dateRange[0].startOf('day');
      const endDate = filters.dateRange[1].endOf('day');
      filtered = filtered.filter(doc => {
        const issueDate = dayjs(doc.issueDate);
        return issueDate.isAfter(startDate) && issueDate.isBefore(endDate);
      });
    }

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(doc =>
        doc.wzNumber.toLowerCase().includes(searchLower) ||
        doc.recipientName.toLowerCase().includes(searchLower) ||
        doc.recipientCompany?.toLowerCase().includes(searchLower) ||
        doc.orderNumber.toLowerCase().includes(searchLower) ||
        doc.displayOrderNumber.toLowerCase().includes(searchLower) ||
        doc.clientOrderNumber?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [documentsByView, listView, filters]);

  const clearFilters = () => {
    setFilters({
      dateRange: null,
      search: ''
    });
  };

  const activeFilterCount = [
    Boolean(filters.search.trim()),
    Boolean(filters.dateRange && filters.dateRange[0] && filters.dateRange[1]),
  ].filter(Boolean).length;

  const getRowMenuItems = (record: WZDocumentDto): MenuProps['items'] => {
    const items: NonNullable<MenuProps['items']> = [
      { key: 'view', icon: <EyeOutlined />, label: t('common:open') },
      { key: 'preview', icon: <FileOutlined />, label: t('wz:actions.preview') },
    ];

    if (canEdit && canShowWZEditAction(record.status)) {
      items.push({ key: 'edit', icon: <EditOutlined />, label: t('common:edit') });
    }

    if (canDownloadWZPdf) {
      items.push({ key: 'pdf', icon: <FilePdfOutlined />, label: t('wz:actions.downloadPDF') });
    }

    if (canDelete) {
      // Backend-computed reason (list rows carry status-only ones): a released
      // WZ keeps its delete entry, disabled, with the explanation on hover.
      const blocked = findBlockedAction(record.blockedActions, 'delete');
      items.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        label: blocked
          ? <Tooltip title={getBlockedActionMessage(blocked, t, 'wz', WZ_ERROR_CODE_KEYS, { siblings: record.blockedActions })}><span>{t('common:delete')}</span></Tooltip>
          : t('common:delete'),
        danger: true,
        disabled: Boolean(blocked),
      });
    }

    return items;
  };

  const columns = [
    {
      title: t('common:actions'),
      key: 'actions',
      width: 56,
      align: 'center' as const,
      fixed: 'left' as const,
      render: (_: unknown, record: WZDocumentDto) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: getRowMenuItems(record),
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();

              if (key === 'view') {
                navigate(`/app/wz/${record.id}`);
              }

              if (key === 'preview') {
                handlePreviewDocument(record);
              }

              if (key === 'edit') {
                navigate(`/app/wz/${record.id}?mode=edit`);
              }

              if (key === 'pdf') {
                void handleDownloadPDF(record.id, record.wzNumber);
              }

              if (key === 'delete') {
                modal.confirm({
                  title: t('wz:confirm.delete'),
                  okText: t('common:yes'),
                  cancelText: t('common:no'),
                  okButtonProps: { danger: true },
                  onOk: () => { void handleDelete(record.id, record.wzNumber); },
                });
              }
            },
          }}
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(event) => event.stopPropagation()}
            aria-label={t('common:actions')}
          />
        </Dropdown>
      ),
    },
    {
      title: t('wz:fields.wzNumber'),
      dataIndex: 'wzNumber',
      key: 'wzNumber',
      width: 186,
      sorter: (a: WZDocumentDto, b: WZDocumentDto, sortOrder?: 'ascend' | 'descend' | null) =>
        compareMonthlyDocumentNumbers(a.wzNumber, b.wzNumber, sortOrder),
      defaultSortOrder: 'descend' as const,
      render: (_: unknown, record: WZDocumentDto) => {
        const cancelledKind = getCancelledDocumentKind(record);
        const costPending = Boolean(record.pendingCostMarker?.costPending);
        const orderNumber = record.displayOrderNumber || record.orderNumber;
        return (
          <ListIdentity
            name={record.wzNumber || '-'}
            onOpen={() => navigate(`/app/wz/${record.id}`)}
            tags={cancelledKind || costPending ? (
              <>
                {cancelledKind && (
                  <Tag color={cancelledKind === 'STORNO' ? 'warning' : 'error'} className="wz-documents-table__cancelled-kind">
                    {t(`wz:cancelledKind.${cancelledKind.toLowerCase()}`)}
                  </Tag>
                )}
                {costPending && <Tag color="warning">{t('wz:pendingPurchaseCostShort')}</Tag>}
              </>
            ) : null}
            // The WZ belongs to one ZK: its number sits on the meta line, as on „Dokumenty”.
            meta={[
              orderNumber ? (
                <button
                  type="button"
                  className="app-page__cell-link"
                  onClick={() => navigate(`/app/orders/${record.orderId}`)}
                >
                  {t('wz:list.order', { number: orderNumber })}
                </button>
              ) : null,
            ]}
          />
        );
      },
    },
    {
      title: t('wz:fields.issueDate'),
      dataIndex: 'issueDate',
      key: 'issueDate',
      width: 132,
      render: (date: string) => <span className="app-page__num">{formatDateEuropean(date)}</span>,
      sorter: (a: WZDocumentDto, b: WZDocumentDto) => dayjs(a.issueDate).unix() - dayjs(b.issueDate).unix(),
    },
    {
      title: t('wz:fields.recipient'),
      key: 'recipient',
      width: 196,
      render: (_: unknown, record: WZDocumentDto) => (
        <div>
          <span className="app-page__party" title={record.recipientName || undefined}>{record.recipientName || '-'}</span>
          {record.recipientCompany && (
            <span className="app-page__subline" title={record.recipientCompany}>{record.recipientCompany}</span>
          )}
        </div>
      ),
    },
    {
      // Shown where operators browse first: this link is what blocks deleting
      // the transport (WZ_TRANSPORT_LINK_EXISTS). Route number as a link, the
      // carrier muted underneath, further trucks as „+N” with a title tooltip.
      title: t('wz:fields.linkedTransports'),
      key: 'linkedTransports',
      width: 176,
      render: (_: unknown, record: WZDocumentDto) => {
        if (!record.linkedTransports || record.linkedTransports.length === 0) {
          return <span className="app-page__muted">—</span>;
        }
        const [first, ...rest] = record.linkedTransports;
        return (
          <div>
            <button
              type="button"
              className="app-page__cell-link"
              onClick={() => navigate(`/app/transports/${first.id}`)}
            >
              {getTransportLabel(first, '-')}
            </button>
            {rest.length > 0 && (
              <span
                className="app-page__muted"
                title={record.linkedTransports.map((transport) => getTransportLabel(transport)).join(', ')}
              >
                {' '}+{rest.length}
              </span>
            )}
            {first.transportCompany && (
              <span className="app-page__subline" title={first.transportCompany}>{first.transportCompany}</span>
            )}
          </div>
        );
      },
    },
    {
      title: t('wz:fields.items'),
      key: 'items',
      width: 226,
      render: (_: unknown, record: WZDocumentDto) => {
        if (!record.items || record.items.length === 0) return <span className="app-page__muted">-</span>;
        const itemNames = record.items.map(item => item.productName);
        // First two lines, each with its dimensions beneath; the rest as „+N”.
        const shown = record.items.slice(0, 2);
        const rest = record.items.length - shown.length;
        return (
          <div className="app-page__lines" title={itemNames.join(', ')}>
            {shown.map((item, index) => (
              <ProductNameWithDimensions
                key={item.id || index}
                name={`${item.productName}${index === shown.length - 1 && rest > 0 ? ` +${rest}` : ''}`}
                dimensions={item}
                dimensionsTestId="wz-list-item-dimensions"
              />
            ))}
          </div>
        );
      },
      // One line per product and per dimensions line, cut with an ellipsis; the full list is in `title` (direction 2026-10-19).
    },
  ];
  const tableMinWidth = columns.reduce((sum, column) => sum + (typeof column.width === 'number' ? column.width : 0), 0);
  const advancedFilterCount = filters.dateRange && filters.dateRange[0] && filters.dateRange[1] ? 1 : 0;

  return (
    <div className="app-page app-page--modern wz-documents-page">
      <WarehouseDocumentsTabs active="wz" />
      <section className="app-page__card">
        <div className="app-page__toolbar">
          <Input
            className="app-page__toolbar-search"
            placeholder={t('wz:filters.search')}
            prefix={<SearchOutlined />}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            allowClear
          />
          <div className="app-page__toolbar-actions">
            <Badge count={advancedFilterCount} size="small" offset={[-2, 2]}>
              <Button
                icon={<FilterOutlined />}
                type={filtersOpen || advancedFilterCount > 0 ? 'primary' : 'default'}
                ghost={filtersOpen || advancedFilterCount > 0}
                onClick={() => setFiltersOpen((current) => !current)}
              >
                {t('wz:filtersHeading')}
              </Button>
            </Badge>
            {activeFilterCount > 0 && (
              <Button type="link" onClick={clearFilters}>
                {t('common:clearFilters')}
              </Button>
            )}
            <Segmented<WarehouseDocumentListView>
              aria-label={t('wz:listView.label')}
              value={listView}
              onChange={handleListViewChange}
              options={[
                { value: 'active', label: t('wz:listView.active') },
                { value: 'cancelled', label: `${t('wz:listView.cancelled')} (${cancelledCount})` },
              ]}
            />
            <span className="app-page__count" aria-label={t('wz:totalDocuments', { total: filteredDocuments.length })}>
              {filteredDocuments.length}
            </span>
          </div>
        </div>

        {filtersOpen && (
          <Row gutter={[12, 12]} className="app-page__filters">
            <Col xs={24} sm={12} md={8} lg={6}>
              <RangePicker
                value={filters.dateRange}
                onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
                format="DD.MM.YYYY"
                placeholder={[t('wz:filters.dateFrom'), t('wz:filters.dateTo')]}
              />
            </Col>
          </Row>
        )}

        <Table
          className="wz-documents-table app-page__list-table"
          columns={columns}
          dataSource={filteredDocuments}
          rowKey="id"
          loading={loading}
          tableLayout="fixed"
          scroll={{ x: tableMinWidth }}
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => t('common:totalItems', { count: total }),
          }}
        />
      </section>

      <Modal
        title={t('wz:actions.documentPreview')}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        maskClosable={false}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            {t('common:close')}
          </Button>,
          ...(canDownloadWZPdf ? [
            <Button
              key="download"
              type="primary"
              icon={<FilePdfOutlined />}
              onClick={() => previewDoc && handleDownloadPDF(previewDoc.id, previewDoc.wzNumber)}
              loading={downloading === previewDoc?.id}
            >
              {t('wz:actions.downloadPDF')}
            </Button>
          ] : [])
        ]}
        width={800}
      >
        {previewDoc && (
          <div className="wz-documents-preview">
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label={t('wz:fields.wzNumber')}>{previewDoc.wzNumber}</Descriptions.Item>
              <Descriptions.Item label={t('wz:fields.orderNumber')}>
                {previewDoc.displayOrderNumber || previewDoc.orderNumber || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('wz:fields.issueDate')}>{formatDateEuropean(previewDoc.issueDate)}</Descriptions.Item>
              <Descriptions.Item label={t('wz:fields.recipient')}>{previewDoc.recipientName}</Descriptions.Item>
            </Descriptions>

            {previewDoc.attachments && previewDoc.attachments.length > 0 && (
              <>
                <Divider orientation="left">{t('wz:fields.attachments')}</Divider>
                <ul className="wz-documents-preview__files">
                  {previewDoc.attachments.map((attachment, index: number) => (
                    <li key={index} className="wz-documents-preview__file">
                      <FileOutlined />
                      <span>{attachment.fileName}</span>
                      <Button
                        type="link"
                        onClick={() => window.open(getAuthFileUrl(attachment.filePath), '_blank')}
                      >
                        {t('wz:actions.openFile')}
                      </Button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default WZDocuments;
